import type { LucideIcon } from 'lucide-react'
import { FilePlus2Icon, MapPinIcon, ShoppingBagIcon } from 'lucide-react'

import type { CertificateActivationTrigger } from 'hooks/api/useCertificateTriggerSettings'

export type ActivationTriggerOption = {
  id: CertificateActivationTrigger
  title: string
  description: string
  best_for: string
  /**
   * The trigger's glyph, carried HERE rather than chosen per surface.
   *
   * The same three options are drawn on five surfaces — the Features cards, the
   * billing select, the inherited-value row, the detail-page cell and the picker
   * modal — and a trigger that is a shopping bag in one place and a tag in
   * another stops being a recognisable thing. One field, so all five agree by
   * construction and a fourth trigger cannot be added without choosing one.
   *
   * Each names the MOMENT, not the object: a document coming into existence, a
   * sale, an arrival somewhere.
   *
   * That rule is why the third title is 'On location arrival' and not 'At
   * location' — see the AT_LOCATION entry below, which records the rename.
   */
  icon: LucideIcon
  /**
   * Reserved but inert — rendered disabled with a "coming soon" chip.
   *
   * Nothing carries this today: AT_LOCATION (formerly AT_STORE), the only value
   * that ever did, went live with VERA-931. Kept because the NEXT reserved trigger will need it, and the
   * disabled treatment it drives is already built and tested.
   */
  comingSoon?: boolean
}

/**
 * Canonical activation-trigger options — the single source of truth shared by the
 * Features page and the Billing overview (so the two surfaces never drift).
 * Mirrors the backend CertificateActivationTriggerEnum. The company's choice
 * (`certificate_trigger_settings.activation_trigger`) drives WHEN a certificate
 * activates and is therefore metered/billed.
 */
export const ACTIVATION_TRIGGERS: ActivationTriggerOption[] = [
  {
    id: 'ON_CREATION',
    icon: FilePlus2Icon,
    title: 'On creation',
    description: 'Certificates activate automatically as soon as they are created.',
    best_for: 'Best for tracking complex supply chains',
  },
  {
    id: 'ON_PRIMARY_TRANSFER',
    icon: ShoppingBagIcon,
    title: 'On sale',
    description: 'Certificates activate when the customer claims ownership.',
    best_for: 'Best for selling DTC products to align costs with sales',
  },
  {
    id: 'AT_LOCATION',
    icon: MapPinIcon,
    title: 'On location arrival',
    description:
      'Certificates activate when one of your locations confirms the item arrived. Send items to a location from the certificate, and confirm arrival there.',
    best_for: 'Best for tracking retail or production hand-offs',
    /*
      VRG-573 — RENAMED from 'At location' to 'On location arrival' (2026-09-05).
      The three titles read 'On creation / On sale / At location': two moments and
      one place. The docstring on `ActivationTriggerOption` above states the rule
      this broke — each title names the MOMENT, "an arrival somewhere" — and the
      old label named where the item is rather than when it becomes billable.

      ⚠️ 'On arrival' was considered and REJECTED, and this is the obvious
      "tightening" someone will otherwise attempt later. Arrival WHERE? A reader
      can take it as arrival at the CUSTOMER, which is a different and later
      moment — that is ON_PRIMARY_TRANSFER. This is a BILLING trigger, so a
      misread costs money. 'On location arrival' spends one word to say the
      arrival is at YOUR location, and that word is load-bearing. Do not drop it.

      The ENUM VALUE `AT_LOCATION` is unchanged and must stay unchanged — it is a
      persisted database value (migration 0101). Only the human-facing label moved.
      The vera.inc pricing page carries the same label and was changed in the same
      pass, so the two surfaces cannot drift.
    */
    // VERA-931 — LIVE as of 2026-09-02, and widened from stores to ANY address
    // (migration 0101). `comingSoon` is gone rather than flipped to
    // false: the flag exists to mark a reserved-but-inert value, and this one is
    // neither. The signal path it was waiting for now exists end to end — dispatch
    // opens a custody hop, the store confirms arrival, and THAT receipt activates
    // and meters the certificate.
  },
]

export const DEFAULT_ACTIVATION_TRIGGER: CertificateActivationTrigger =
  'ON_PRIMARY_TRANSFER'

export const activationTriggerTitle = (
  trigger: CertificateActivationTrigger | null | undefined,
): string =>
  ACTIVATION_TRIGGERS.find(option => option.id === trigger)?.title ??
  ACTIVATION_TRIGGERS.find(option => option.id === DEFAULT_ACTIVATION_TRIGGER)!
    .title
